import * as React from 'react';
import type { CanvasDraftKind } from '@hector/shared-types';
import type { SuperDocInstance } from '@superdoc/react';
import { canvasDocumentRuntime } from '../state/canvasDocumentRuntime';
import { useCanvasDocumentScope } from '../state/canvasDocumentScopeContext';
import type { CanvasDraftControllerSnapshot } from '../state/canvasDraftController';

export interface UseCanvasAutoSaveParams {
  instance: SuperDocInstance | null;
  /**
   * LE PORTEUR, tel que le panneau l'a reçu — jamais relu sur le scope.
   *
   * ⚠️ C'est la SECONDE source de la garde ci-dessous (#1336) : le scope arrive
   * par le contexte React, ce champ par la chaîne de props. Y remettre
   * `scope.conversationId` comparerait une valeur à elle-même.
   */
  conversationId: string;
  caseId: string | null;
  kind: CanvasDraftKind;
  title: string;
  sourceDocumentId: string | null;
  restoredDraftId?: string | null;
  /** Server ACK identity to retain when mounting an already persisted draft. */
  restoredDraftIntegrityAttemptId?: string | null;
}

/** Flushes only the explicitly addressed logical document. */
export function flushCanvasDraftNow(conversationId: string): void {
  canvasDocumentRuntime.getDraftController(conversationId)?.flush();
}

/**
 * Thin React adapter around the draft controller owned by the exact scope.
 *
 * All mutable autosave state lives below React, keyed by conversation. This
 * hook only binds the current physical instance and metadata, subscribes the
 * indicator, and flushes that same scope when its resident unmounts.
 */
export function useCanvasAutoSave(params: UseCanvasAutoSaveParams): CanvasDraftControllerSnapshot {
  const scope = useCanvasDocumentScope();
  const controller = scope.getDraftController();

  /**
   * ⚠️ **Cette garde était à moitié désarmée par son unique appelant** (#1336).
   * Le panneau passait `state.documentSessionId ?? scope.documentSessionId` :
   * dès que l'état de streaming n'affichait aucun travail — le cas ordinaire —
   * la valeur comparée était celle du scope lui-même, donc la garde ne pouvait
   * pas échouer. Elle ne mordait que pendant un flux. Depuis que le porteur
   * descend par les props sans repli, les deux termes ont bien deux origines.
   */
  if (scope.conversationId !== params.conversationId) {
    throw new Error(
      `Canvas autosave binding refused: le scope fourni appartient à la conversation `
      + `${scope.conversationId}, mais l'appelant a demandé la conversation `
      + `${params.conversationId}. Rends le panneau sous le scope qui possède ce `
      + 'document avant de câbler l\'enregistrement automatique.',
    );
  }

  React.useLayoutEffect(() => {
    const ownerDecision = controller.configureContext({
      conversationId: params.conversationId,
      caseId: params.caseId,
      kind: params.kind,
      title: params.title,
      sourceDocumentId: params.sourceDocumentId,
    });
    if (!ownerDecision.accepted) return;
    controller.configureInstance(params.instance);
    if (params.restoredDraftId) {
      controller.restoreDraft(
        params.restoredDraftId,
        params.conversationId,
        params.restoredDraftIntegrityAttemptId ?? undefined,
      );
    }
  }, [
    controller,
    params.caseId,
    params.conversationId,
    params.instance,
    params.kind,
    params.restoredDraftId,
    params.restoredDraftIntegrityAttemptId,
    params.sourceDocumentId,
    params.title,
  ]);

  React.useEffect(() => () => controller.flush(), [controller]);

  return React.useSyncExternalStore(
    React.useCallback((listener) => controller.subscribe(listener), [controller]),
    React.useCallback(() => controller.getSnapshot(), [controller]),
    React.useCallback(() => controller.getSnapshot(), [controller]),
  );
}
